export interface PropertyValidator {
  key: string;
  required: boolean;
  validator: (value: unknown) => boolean;
}

export const hasValidProperties = (object: Record<string, any>, validators: PropertyValidator[]) => {
  return validators.every(({ key, required, validator }) => {
    const hasProperty = key in object;
    if (required && !hasProperty) return false;
    if (!hasProperty) return true;
    return validator(object[key]);
  });
};

// ==================== BASIC TYPE VALIDATORS ====================

export const isNumber = (value: unknown): value is number => typeof value === 'number';
export const isString = (value: unknown): value is string => typeof value === 'string';
export const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean';

// ==================== OPTIONAL TYPE VALIDATORS (allows undefined) ====================

export const isOptionalNumber = (value: unknown): boolean =>
  typeof value === 'number' || value === undefined;
export const isOptionalString = (value: unknown): boolean =>
  typeof value === 'string' || value === undefined;
export const isOptionalBoolean = (value: unknown): boolean =>
  typeof value === 'boolean' || value === undefined;

// ==================== NULLABLE TYPE VALIDATORS (allows null) ====================

export const isNullableNumber = (value: unknown): boolean =>
  typeof value === 'number' || value === null;
export const isNullableString = (value: unknown): boolean =>
  typeof value === 'string' || value === null;

// ==================== OPTIONAL + NULLABLE VALIDATORS (allows both undefined and null) ====================

export const isOptionalNullableString = (value: unknown): boolean =>
  typeof value === 'string' || value === undefined || value === null;
export const isOptionalNullableNumber = (value: unknown): boolean =>
  typeof value === 'number' || value === undefined || value === null;

// ==================== ARRAY VALIDATORS ====================

export const isNumberArray = (value: unknown): boolean =>
  Array.isArray(value) && value.every(item => typeof item === 'number');
export const isOptionalNullableNumberArray = (value: unknown): boolean =>
  value === undefined || value === null || isNumberArray(value);

// ==================== ENUM VALIDATOR GENERATOR ====================

/**
 * Creates a validator for enum values
 * @param enumObject The enum object to validate against
 * @returns A validator function for the enum
 */
export const createEnumValidator = <T extends Record<string, string | number>>(enumObject: T) =>
  (value: unknown): boolean =>
    (typeof value === 'string' || typeof value === 'number') && Object.values(enumObject).includes(value as any);

/**
 * Creates an optional enum validator (allows undefined)
 * @param enumObject The enum object to validate against
 * @returns A validator function for optional enum values
 */
export const createOptionalEnumValidator = <T extends Record<string, string | number>>(enumObject: T) =>
  (value: unknown): boolean =>
    value === undefined || createEnumValidator(enumObject)(value);

/**
 * Creates an optional + nullable enum validator (allows undefined and null)
 * @param enumObject The enum object to validate against
 * @returns A validator function for optional nullable enum values
 */
export const createOptionalNullableEnumValidator = <T extends Record<string, string | number>>(enumObject: T) =>
  (value: unknown): boolean =>
    value === undefined || value === null || createEnumValidator(enumObject)(value);

